// The Manager Entry export, on screen — L-26, owner-released 2026-08-18.
//
// His question was "where does all the Manager entry items for payroll land?"
// and the answer, until now, was "in Neon, and nowhere a person can read it".
// This is the readable half: ONE report per clinic per pay period, showing
// EVERY submission for that period — all six money sections and all three
// roster sections, voided lines included and marked VOID.
//
// It is generated on demand from the same endpoint the CSV comes from, so the
// screen and the file the payroll agent fetches cannot disagree. Nothing here
// computes a total: the server does, and a second opinion about payroll
// arithmetic is worth less than none.
import type { JSX } from 'preact';
import { Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  loadManagerEntryReport, fetchReportCsv, csvFilename, postExportNow, setRosterEnteredInAdp,
  extraFieldLabel,
  NEW_HIRE_EXTRA_LABELS,
  type ManagerEntryReport as Report,
  type ReportMoneySection, type ReportRosterSection, type ReportRosterRow,
  type PayrollExportResult,
  rosterDetailRequest,
} from '../lib/managerEntryReport';
import { useTableSort } from '../lib/tableSort';
import { SortableTh } from './SortableTh';
import { session } from '../lib/session';
import { ApiError } from '../lib/apiClient';
import { revealRosterSecure, type SecureDetails } from '../lib/managerEntry';

/**
 * OWNER only, and not while viewing-as a single clinic (same narrowing
 * ReportScheduleBoard's pause switch uses): the export writes every clinic's
 * files, not just the one in view, so an owner scoped into MEWP should not
 * see a button that reaches past it.
 */
function canRunExportNow(
  me: { role?: string | null; viewingAs?: boolean } | null | undefined = session.value.me,
): boolean {
  if (!me || me.viewingAs) return false;
  return me.role === 'OWNER';
}

/**
 * "Mark as entered [in ADP]" (owner, 2026-09-22) — OWNER/DIRECTOR only, same
 * split as everywhere else in this repo that keys off isOwnerOrDirector on
 * the server; this only hides the button from a role that would get a 403.
 */
function canMarkEnteredInAdp(
  me: { role?: string | null } | null | undefined = session.value.me,
): boolean {
  return Boolean(me && (me.role === 'OWNER' || me.role === 'DIRECTOR'));
}

export function ManagerEntryReport(
  { clinic, periodId: formPeriodId }: { clinic: string; periodId: number | null },
): JSX.Element | null {
  // A name clicked in the Submitted table (rosterDetailRequest) may belong to
  // another week than the form's; the box shows that week until the form's
  // week changes.
  const [focusPeriodId, setFocusPeriodId] = useState<number | null>(null);
  useEffect(() => { setFocusPeriodId(null); }, [formPeriodId, clinic]);
  const periodId = focusPeriodId ?? formPeriodId;
  const request = rosterDetailRequest.value;
  const sectionRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportResult, setExportResult] = useState<PayrollExportResult | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  // Roster detail panel (N-109): which row is open, paired with its
  // section so the panel knows the change type and field labels without a
  // second lookup. Any "mark entered" toggle anywhere on this report
  // updates `report` in place, so the open panel and the table row it came
  // from never disagree about the state just written.
  const [detailRow, setDetailRow] = useState<
    { row: ReportRosterRow; section: ReportRosterSection } | null
  >(null);

  /** Applied wherever a roster row's entered-in-ADP state changes, so the
   * table, the detail panel and any other section stay in sync without a
   * full report re-fetch (INV-1 — no extra network call for a toggle). */
  function applyEnteredInAdp(
    id: number, enteredInAdpAt: string | null, enteredInAdpBy: string | null,
  ): void {
    setReport((prev) => {
      if (!prev) return prev;
      const rosterSections = prev.rosterSections.map((s) => ({
        ...s,
        rows: s.rows.map((r) => (r.id === id ? { ...r, enteredInAdpAt, enteredInAdpBy } : r)),
      }));
      return { ...prev, rosterSections };
    });
    setDetailRow((prev) => (prev && prev.row.id === id
      ? { ...prev, row: { ...prev.row, enteredInAdpAt, enteredInAdpBy } }
      : prev));
  }

  // Re-fetched whenever the clinic or the period changes, and only while open:
  // this is a report somebody asks for, not a fifth thing the page loads on
  // arrival (INV-1 budgets navigation at zero network calls).
  useEffect(() => {
    if (!open || periodId === null) return;
    let live = true;
    setError(null);
    setBusy(true);
    loadManagerEntryReport(clinic, periodId)
      .then((r) => { if (live) setReport(r); })
      .catch((e: Error) => { if (live) setError(e.message); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [open, clinic, periodId]);

  useEffect(() => {
    if (!request) return;
    if (request.payPeriodId !== periodId) { setFocusPeriodId(request.payPeriodId); return; }
    if (!open) { setOpen(true); return; }
    if (!report || busy) return;
    for (const section of report.rosterSections) {
      const row = section.rows.find((r) => r.id === request.rosterId);
      if (row) {
        setDetailRow({ row, section });
        sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        break;
      }
    }
    rosterDetailRequest.value = null;
  }, [request, periodId, open, report, busy]);

  async function download(): Promise<void> {
    if (periodId === null || !report) return;
    setBusy(true);
    setError(null);
    try {
      const csv = await fetchReportCsv(clinic, periodId);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = csvFilename(report);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * The button (payroll-017): runs the Tuesday job right now instead of
   * waiting for it. Never silent — a business "no" (`ran: false`) renders
   * the job's own `reason`, and a thrown ApiError renders its message, the
   * same way every other action on this component already does.
   */
  async function writeToPayrollFolder(): Promise<void> {
    if (periodId === null) return;
    setExportBusy(true);
    setExportError(null);
    setExportResult(null);
    try {
      const result = await postExportNow(periodId);
      setExportResult(result);
    } catch (e) {
      setExportError((e as Error).message);
    } finally {
      setExportBusy(false);
    }
  }

  if (periodId === null) return null;

  return (
    // Its own box (owner, 2026-09-24).
    <section class="card entry-section" data-testid="manager-entry-report" ref={sectionRef}>
      <div class="section-head">
        <h3>Payroll export</h3>
        <button
          type="button" class="btn"
          aria-expanded={open}
          data-testid="manager-entry-report-toggle"
          onClick={() => setOpen(!open)}
        >
          {open ? 'Hide' : 'Show'} export
        </button>
        {canRunExportNow() ? (
          <button
            type="button" class="btn" disabled={exportBusy}
            data-testid="manager-entry-export-now"
            onClick={() => void writeToPayrollFolder()}
          >
            Write to payroll folder
          </button>
        ) : null}
      </div>

      {exportBusy ? (
        <p role="status" data-testid="manager-entry-export-now-busy">Writing the payroll files…</p>
      ) : null}

      {exportError ? (
        <small class="bad" role="alert" data-testid="manager-entry-export-now-error">{exportError}</small>
      ) : null}

      {exportResult && !exportResult.ran ? (
        <small class="bad" role="alert" data-testid="manager-entry-export-now-refused">
          {exportResult.reason}
        </small>
      ) : null}

      {exportResult && exportResult.ran ? (
        <div data-testid="manager-entry-export-now-result">
          <p>
            Week {exportResult.week} written
            {exportResult.held.length ? ` — ${exportResult.held.length} row(s) held back` : ''}.
          </p>
          <ul>
            {exportResult.clinics.map((c) => (
              <li key={c.locationCode} data-testid={`manager-entry-export-now-clinic-${c.locationCode}`}>
                <strong>{c.locationCode}</strong>
                {c.files.length
                  ? `: ${c.files.map((f) => f.name).join(', ')}`
                  : c.reason ? `: ${c.reason}` : ': no files written'}
              </li>
            ))}
          </ul>
          {exportResult.held.length ? (
            <ul>
              {exportResult.held.map((h) => (
                <li key={h} class="bad" data-testid="manager-entry-export-now-held">{h}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {!open ? (
        <p>
          Everything entered for this clinic and pay period, laid out by ADP pay code —
          the sheet the payroll agent works from.
        </p>
      ) : null}

      {open && error ? (
        <small class="bad" role="alert" data-testid="manager-entry-report-error">{error}</small>
      ) : null}

      {open && !report && busy ? <p role="status">Building the report…</p> : null}

      {open && report ? (
        <>
          <div class="section-head">
            <h3>
              {report.location.name} — {report.payPeriod.label}
            </h3>
            <button
              type="button" class="btn" disabled={busy}
              data-testid="manager-entry-report-csv"
              onClick={() => void download()}
            >
              Download CSV
            </button>
          </div>

          <p data-testid="manager-entry-report-counts">
            {report.reconciliation.submissionCount} submission(s) ·{' '}
            {report.totals.activeLineCount} live money line(s)
            {report.totals.voidedLineCount
              ? ` · ${report.totals.voidedLineCount} voided`
              : ''} ·{' '}
            {report.totals.rosterCount} roster change(s)
          </p>

          {/* The completeness check, in front of the reader rather than in a
              log. It compares the rows found against the counts each
              submission recorded when it was written, so a line lost between
              then and now is visible to the person about to key payroll. */}
          {!report.reconciliation.complete ? (
            <p class="bad" role="alert" data-testid="manager-entry-report-incomplete">
              This report does not reconcile: the submissions recorded{' '}
              {report.reconciliation.declaredLineCount} money line(s) and{' '}
              {report.reconciliation.declaredRosterCount} roster change(s), but{' '}
              {report.reconciliation.foundLineCount} and{' '}
              {report.reconciliation.foundRosterCount} were found. Do not key this
              payroll from it — tell Tomer.
            </p>
          ) : null}

          {report.moneySections.map((section) => (
            <ReportMoneySectionTable key={section.key} section={section} />
          ))}

          {report.unclassified.length ? (
            <div class="entry-section" data-testid="report-unclassified">
              <div class="section-head">
                <h3 class="bad">Lines in no known section</h3>
                <p>
                  These were submitted but match none of the six pay codes. They are
                  shown rather than dropped — tell Tomer before keying payroll.
                </p>
              </div>
              <ul>
                {report.unclassified.map((r) => (
                  <li key={r.id}>
                    {r.employeeName ?? `Employee #${r.employeeId}`} — {r.amount} — {r.category}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {report.rosterSections.map((section) => (
            <ReportRosterSectionTable
              key={section.key}
              section={section}
              onOpenDetail={(row) => setDetailRow({ row, section })}
              onEnteredInAdp={applyEnteredInAdp}
            />
          ))}
        </>
      ) : null}

      {detailRow ? (
        <RosterDetailPanel
          row={detailRow.row}
          section={detailRow.section}
          onClose={() => setDetailRow(null)}
          onEnteredInAdp={applyEnteredInAdp}
        />
      ) : null}
    </section>
  );
}

/** One pay-code section of the export, sortable by heading (owner,
 * 2026-08-19). Its own component so the sort hook has somewhere to live. */
function ReportMoneySectionTable({ section }: { section: ReportMoneySection }): JSX.Element {
  const sorted = useTableSort(section.rows, {
    employee: (r) => r.employeeName,
    amount: (r) => (r.amount === null ? null : Number(r.amount)),
    detail: (r) => r.detail,
    by: (r) => r.enteredBy,
    status: (r) => (r.void ? 'VOID' : 'Active'),
  });
  return (
    <div class="entry-section" data-testid={`report-${section.key}`}>
              <div class="section-head">
                <h3>
                  {section.title}
                  <span class="role-chip"> {section.payCode}</span>
                </h3>
              </div>
              {section.rows.length === 0 ? (
                // Empty sections stay on the report on purpose: "did anyone
                // submit uniform deductions?" must be answerable from it.
                <p>None this period.</p>
              ) : (
                <div class="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <SortableTh label="Employee" sortKey="employee" state={sorted.state} onSort={sorted.toggle} />
                        <SortableTh label="Amount" sortKey="amount" state={sorted.state} onSort={sorted.toggle} className="num" />
                        <SortableTh label={section.detailLabel} sortKey="detail" state={sorted.state} onSort={sorted.toggle} />
                        <SortableTh label="Entered by" sortKey="by" state={sorted.state} onSort={sorted.toggle} />
                        <SortableTh label="Status" sortKey="status" state={sorted.state} onSort={sorted.toggle} />
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.rows.map((r) => (
                        <tr key={r.id} class={r.void ? 'voided' : undefined}>
                          <td>{r.employeeName ?? `Employee #${r.employeeId}`}</td>
                          <td class="num">
                            {section.kind === 'DEDUCTION' ? `(${r.amount})` : r.amount}
                          </td>
                          <td>{r.detail || '—'}</td>
                          <td>{r.enteredBy}</td>
                          <td>
                            {r.void ? <span class="role-chip">VOID</span> : 'Active'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total (live lines only)</th>
                        <td class="num" data-testid={`report-total-${section.key}`}>
                          {section.kind === 'DEDUCTION' ? `(${section.total})` : section.total}
                        </td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
  );
}

/** Displayed name: the resolved employee, or (NEW_HIRE only) the name the
 * manager typed for someone not on the roster yet (046_new_hire_names.sql). */
function rosterDisplayName(r: ReportRosterRow): string {
  if (r.employeeName) return r.employeeName;
  const typed = [r.firstName, r.middleName, r.lastName].filter((s) => s && s.trim()).join(' ');
  return typed || `Employee #${r.employeeId}`;
}

/** One roster section of the export, sortable by heading. "On file" sorts on
 * whether a restricted field was captured — never on a value (SPEC-001 §7). */
function ReportRosterSectionTable(
  { section, onOpenDetail, onEnteredInAdp }: {
    section: ReportRosterSection;
    onOpenDetail: (row: ReportRosterRow) => void;
    onEnteredInAdp: (id: number, enteredInAdpAt: string | null, enteredInAdpBy: string | null) => void;
  },
): JSX.Element {
  const sorted = useTableSort(section.rows, {
    employee: (r) => rosterDisplayName(r),
    field1: (r) => r.field1,
    field2: (r) => r.field2,
    onfile: (r) => Boolean(r.ssnProvided || r.bankProvided),
    by: (r) => r.enteredBy,
    status: (r) => (r.void ? 'VOID' : 'Active'),
    entered: (r) => Boolean(r.enteredInAdpAt),
  });
  return (
    <div class="entry-section" data-testid={`report-${section.key}`}>
              <div class="section-head">
                <h3>{section.title}</h3>
              </div>
              {section.rows.length === 0 ? (
                <p>None this period.</p>
              ) : (
                <div class="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <SortableTh label="Employee" sortKey="employee" state={sorted.state} onSort={sorted.toggle} />
                        <SortableTh label={section.field1Label} sortKey="field1" state={sorted.state} onSort={sorted.toggle} />
                        <SortableTh label={section.field2Label} sortKey="field2" state={sorted.state} onSort={sorted.toggle} />
                        <SortableTh label="On file" sortKey="onfile" state={sorted.state} onSort={sorted.toggle} />
                        <SortableTh label="Entered by" sortKey="by" state={sorted.state} onSort={sorted.toggle} />
                        <SortableTh label="Status" sortKey="status" state={sorted.state} onSort={sorted.toggle} />
                        <SortableTh label="In ADP" sortKey="entered" state={sorted.state} onSort={sorted.toggle} />
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.rows.map((r) => (
                        <tr key={r.id} class={r.void ? 'voided' : undefined}>
                          <td>
                            <button
                              type="button"
                              class="link"
                              data-testid={`report-roster-${r.id}-open`}
                              onClick={() => onOpenDetail(r)}
                            >
                              {rosterDisplayName(r)}
                            </button>
                          </td>
                          <td>{r.field1 || '—'}</td>
                          <td>{r.field2 || '—'}</td>
                          <td>
                            {/* Flags only, never a value (SPEC-001 §7). */}
                            {r.ssnProvided ? <span class="role-chip">SSN on file</span> : null}
                            {r.bankProvided ? <span class="role-chip">bank on file</span> : null}
                            {!r.ssnProvided && !r.bankProvided ? '—' : null}
                          </td>
                          <td>{r.enteredBy}</td>
                          <td>{r.void ? <span class="role-chip">VOID</span> : 'Active'}</td>
                          <td>
                            <EnteredInAdpControl row={r} onChange={onEnteredInAdp} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
  );
}

/**
 * The "mark as entered [in ADP]" toggle (owner, 2026-09-22), one per roster
 * row. Reversible — the same control unmarks a row it already marked.
 * OWNER/DIRECTOR only on screen; the server enforces the same gate.
 */
function EnteredInAdpControl(
  { row, onChange }: {
    row: ReportRosterRow;
    onChange: (id: number, enteredInAdpAt: string | null, enteredInAdpBy: string | null) => void;
  },
): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const entered = Boolean(row.enteredInAdpAt);

  async function toggle(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await setRosterEnteredInAdp(row.id, !entered);
      onChange(row.id, result.rosterChange.enteredInAdpAt, result.rosterChange.enteredInAdpBy);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  }

  if (!canMarkEnteredInAdp()) {
    return entered ? (
      <span class="role-chip" data-testid={`report-roster-${row.id}-entered-chip`}>
        Entered
      </span>
    ) : (
      <>—</>
    );
  }

  return (
    <>
      <button
        type="button"
        class={entered ? 'role-chip' : 'btn'}
        disabled={row.void || busy}
        data-testid={`report-roster-${row.id}-entered-toggle`}
        title={entered
          ? `Entered in ADP by ${row.enteredInAdpBy ?? ''}${row.enteredInAdpAt ? ` (${row.enteredInAdpAt})` : ''}`
          : 'Mark as entered in ADP'}
        onClick={() => void toggle()}
      >
        {busy ? 'Saving…' : entered ? 'Entered' : 'Mark entered'}
      </button>
      {error ? <small class="bad" role="alert">{error}</small> : null}
    </>
  );
}

/** 'YYYY-MM-DD' → 'MM/DD/YYYY'; anything else as typed; empty → '—'. */
export function displayValue(v: string | null | undefined): string {
  const t = (v ?? '').trim();
  if (!t) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : t;
}

/** A pay rate as money per hour ('15' → '$15.00 / hr'); non-numbers as typed. */
export function displayRate(v: string | null | undefined): string {
  const n = Number(String(v ?? '').replace(/[$,\s]/g, ''));
  return v && Number.isFinite(n) ? `$${n.toFixed(2)} / hr` : displayValue(v);
}

/** An ISO timestamp in the clinics' own time (Eastern), e.g. '10/20/2026, 1:04 AM'. */
export function displayTimestamp(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/**
 * The roster-change detail panel (N-109, owner-requested 2026-09-22): his
 * words, the manager entry dashboard "can only see five generic columns" —
 * clicking the employee name here opens the whole record, including the
 * new-hire fields (position/address/phone/email/W4) the table never showed.
 * A row with no `extra` (terminations, rate changes) shows the base fields
 * only — no empty "New-hire info" section.
 */
function RosterDetailPanel(
  { row, section, onClose, onEnteredInAdp }: {
    row: ReportRosterRow;
    section: ReportRosterSection;
    onClose: () => void;
    onEnteredInAdp: (id: number, enteredInAdpAt: string | null, enteredInAdpBy: string | null) => void;
  },
): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Readable for keying a person into ADP (owner, 2026-10-20: "its missing
  // email, birthdate ... the screen that opens looks terrible cant even read
  // it"). The fields were all there; the dt/dd pairs flowed through an
  // auto-fill grid meant for wrapped cards, so labels and values landed in
  // different rows. Now a strict two-column list, ADP fields first, in the
  // order ADP asks for them; every `extra` key still shows (unknown ones last,
  // labeled by the key) so nothing a manager typed can go missing.
  const extra = row.extra ?? {};
  const ordered = [
    ...Object.keys(NEW_HIRE_EXTRA_LABELS).filter((k) => k in extra),
    ...Object.keys(extra).filter((k) => !(k in NEW_HIRE_EXTRA_LABELS)),
  ];
  const isRate = /rate/i.test(section.field2Label);

  // Owner, 2026-10-20: "now i cant see the bank details.. just show them in
  // the show export". Opening the record IS the Show: one reveal through the
  // same logged endpoint as the Show button (SPEC-001 §7 — read from
  // employee_secure's staging row, recorded server-side, owner / director /
  // that clinic's manager only; anyone else gets the server's refusal, shown).
  const hasSecure = row.ssnProvided || row.bankProvided;
  const withSsn = section.changeType !== 'DIRECT_DEPOSIT';
  const [secure, setSecure] = useState<SecureDetails | null>(null);
  const [secureError, setSecureError] = useState<string | null>(null);
  useEffect(() => {
    if (!hasSecure) return undefined;
    let live = true;
    revealRosterSecure(row.id)
      .then((d) => { if (live) setSecure(d); })
      .catch((err: unknown) => { if (live) setSecureError(err instanceof Error ? err.message : String(err)); });
    return () => { live = false; };
  }, [row.id, hasSecure]);
  const secureValue = (v: string | null | undefined): string => {
    if (secureError) return 'could not load';
    if (!secure) return 'Loading…';
    return v || '—';
  };

  return (
    <div class="note-drawer-root" data-testid="report-roster-detail">
      <button type="button" class="note-drawer-backdrop" aria-label="Close" onClick={onClose} />
      <aside class="note-drawer" role="dialog" aria-labelledby="report-roster-detail-title">
        <div class="note-drawer-head">
          <button
            type="button"
            class="note-drawer-close"
            aria-label="Close"
            data-testid="report-roster-detail-close"
            onClick={onClose}
          >
            ×
          </button>
          <h3 id="report-roster-detail-title" data-testid="report-roster-detail-title">
            {rosterDisplayName(row)}
          </h3>
        </div>

        <div class="roster-detail">
          <h4>{section.title}</h4>
          <dl class="roster-detail-list" data-testid="report-roster-detail-extra">
            <dt>{section.field1Label}</dt>
            <dd>{displayValue(row.field1)}</dd>
            <dt>{section.field2Label}</dt>
            <dd>{isRate ? displayRate(row.field2) : displayValue(row.field2)}</dd>
            {ordered.map((key) => (
              <Fragment key={key}>
                <dt>{extraFieldLabel(key)}</dt>
                <dd>{displayValue(extra[key])}</dd>
              </Fragment>
            ))}
            {withSsn ? (
              <>
                <dt>SSN</dt>
                <dd data-testid="report-roster-detail-ssn">{row.ssnProvided ? secureValue(secure?.ssn) : 'Not entered'}</dd>
              </>
            ) : null}
            <dt>Bank</dt>
            <dd>{row.bankProvided ? secureValue(secure?.bankName) : 'Not entered'}</dd>
            <dt>Routing number</dt>
            <dd data-testid="report-roster-detail-routing">{row.bankProvided ? secureValue(secure?.routingNumber) : 'Not entered'}</dd>
            <dt>Account number</dt>
            <dd data-testid="report-roster-detail-account">{row.bankProvided ? secureValue(secure?.accountNumber) : 'Not entered'}</dd>
          </dl>
          {secureError ? <p class="bad" data-testid="report-roster-detail-secure-error">{secureError}</p> : null}

          <h4>Entry</h4>
          <dl class="roster-detail-list">
            <dt>Note</dt>
            <dd>{row.note || '—'}</dd>
            <dt>Entered by</dt>
            <dd>{row.enteredBy}</dd>
            <dt>Entered at</dt>
            <dd>{displayTimestamp(row.enteredAt)}</dd>
            <dt>Status</dt>
            <dd>{row.void ? <span class="role-chip">VOID</span> : 'Active'}</dd>
          </dl>

          <h4>ADP</h4>
        <p>
          {row.enteredInAdpAt
            ? `Entered in ADP by ${row.enteredInAdpBy ?? ''} (${displayTimestamp(row.enteredInAdpAt)}).`
            : 'Not yet marked entered in ADP.'}
        </p>
        <EnteredInAdpControl row={row} onChange={onEnteredInAdp} />
        </div>
      </aside>
    </div>
  );
}
